<script>
import validationMessages from '@/components/validations'
import {required} from "vuelidate/lib/validators"
import Monitor from '@/services/Monitor'
import Swal from "sweetalert2"

export default {
  components: {
    validationMessages,
  },
  props: ['tenants', 'configGroups', 'configKeys', 'configDefinitions'],
  data() {
    return {
      showModal: false,
      tryingToSubmit: false,
      submitted: false,
      key: '',
      group: '',
      description: '',
      type: 'string',
      tenantRows: [
        {tenant: '', value: ''}
      ],
      csrf_token: localStorage.getItem('csrf_token')
    };
  },

  validations: {
    key: {required},
    tenantRows: {
      $each: {
        tenant: {required}
      }
    }
  },

  methods: {
    refreshData() {
      this.$emit('onRefresh')
    },

    closeModal() {
      this.showModal = false
    },

    resetProps() {
      this.group = ''
      this.key = ''
      this.description = ''
      this.type = 'string'
      this.tenantRows = [{tenant: '', value: ''}]
      this.submitted = false
    },

    normalizeFlag(value) {
      return value === true || value === 1 || value === '1'
    },

    flagLabel(value) {
      return this.normalizeFlag(value) ? 'Yes' : 'No'
    },

    setDefinitionFields(definition) {
      this.group = definition?.group || ''
      this.type = definition?.type || 'string'
      this.description = definition?.description || ''
    },

    addTenantRow() {
      if (this.tenantRows.length >= this.tenants.length) return;

      this.tenantRows.push({tenant: '', value: ''});
    },

    removeTenantRow(index) {
      if (this.tenantRows.length > 1) {
        this.tenantRows.splice(index, 1);
      }
    },

    getTenantOptions(currentIndex) {
      return this.tenants.map(t => ({
        value: t,
        text: t,
        disabled: this.tenantRows.some((row, index) => {
          return index !== currentIndex && row.tenant === t;
        })
      }));
    },

    async createTenantEnvConfig() {
      this.submitted = true
      this.tryingToSubmit = true

      this.$v.$touch()
      if (this.$v.$invalid) {
        this.tryingToSubmit = false
        return
      }

      const formData = {
        group: this.group,
        key: this.key,
        type: this.type,
        description: this.description,
        values: this.tenantRows.map(r => ({
          key: this.key,
          tenant: r.tenant,
          value: r.value
        }))
      }

      try {
        const response = await Monitor.createTenantEnvConfig(formData)
        if (response.data.data) {
          this.successmsg('Environment Config Values Created!')
          this.refreshData()
          this.closeModal()
        } else {
          this.failedmsg(response.data.error || 'Failed to create Environment Config Values!')
        }
      } catch (error) {
        const resData = error.response?.data?.error || 'Failed'
        const errorMsg = Array.isArray(resData) ? resData[0] : resData
        this.failedmsg(errorMsg)
      } finally {
        this.tryingToSubmit = false
      }
    },

    bulkCreateForAllTenants() {

      document.activeElement?.blur();

      Swal.fire({
        title: "Set Value for All Tenants",
        icon: "info",
        showCancelButton: true,
        confirmButtonText: "Apply",
        cancelButtonText: "Cancel",

        input: "text",
        inputLabel: "Value",
        inputPlaceholder: "Enter value for all tenants",

        focusConfirm: false,

        didOpen: () => {
          setTimeout(() => {
            const input = Swal.getInput();
            if (input) {
              input.focus();
              input.click(); // 🔥 critical for Chrome inside modals
            }
          }, 150);
        },

        inputValidator: (value) => {
          if (!value) return "Value is required!";
        }
      }).then((result) => {
        if (!result.value) return;

        this.applyBulkTenants(result.value);
      });
    },

    applyBulkTenants(bulkValue) {
      this.tenantRows = this.tenants.map(t => {
        const existing = this.tenantRows.find(r => r.tenant === t);

        return existing
            ? existing
            : { tenant: t, value: bulkValue };
      });
    }

  },
  watch: {
    key(newVal) {
      const definition = (this.configDefinitions || []).find(item => item.key === newVal)

      if (definition) {
        this.setDefinitionFields(definition)
      } else {
        this.setDefinitionFields(null)
      }
    },

    tenantRows: {
      handler(rows) {
        const seen = new Set();

        rows.forEach((row, index) => {
          if (!row.tenant) return;

          if (seen.has(row.tenant)) {
            this.$set(this.tenantRows[index], 'tenant', '');
          } else {
            seen.add(row.tenant);
          }
        });
      },
      deep: true
    }

  },
  computed: {
    keyOptions() {
      return [
        {value: '', text: 'Select Key', disabled: true},
        ...(this.configKeys || []).map(key => ({value: key, text: key}))
      ]
    },
    selectedKeyDefinition() {
      if (!this.key || !this.configDefinitions) return null;

      return this.configDefinitions.find(
          item => item.key === this.key
      ) || null;
    }
  }
};
</script>

<template>
  <b-modal id="add_new_env_config" size="lg" v-model="showModal" @hidden="resetProps" title="Add New Environment Config Values"
           title-class="font-18" :no-enforce-focus="true">
    <form @submit.prevent="createTenantEnvConfig">
      <div class="row">

        <div class="col-12">
          <div class="mb-3">
            <label class="control-label form-label">Key Name *</label>
            <b-form-select
                v-model="key"
                :options="keyOptions"
                :class="{ 'is-invalid': submitted && $v.key.$error }"
                class="w-100"
            ></b-form-select>
            <validationMessages v-if="submitted" :fieldName="'Key Name'" :validationName="$v.key"></validationMessages>
          </div>

          <div v-if="selectedKeyDefinition" class="mb-3">
            <p class="mb-1">
              <strong>Type:</strong>
              <span>{{ selectedKeyDefinition.type }}</span>
            </p>

            <p class="mb-1 d-flex flex-wrap gap-3">
              <span><strong>Tenant Required:</strong> {{ flagLabel(selectedKeyDefinition.tenant_required) }}</span>
              <span><strong>Common Required:</strong> {{ flagLabel(selectedKeyDefinition.common_required) }}</span>
              <span><strong>Is Secret:</strong> {{ flagLabel(selectedKeyDefinition.is_secret) }}</span>
            </p>

            <p class="mb-0">
              <strong>Description:</strong>
              {{ selectedKeyDefinition.description }}
            </p>
          </div>

          <div class="row">
            <div class="col-12">
              <h6 class="text-uppercase mb-2">Tenant Values</h6>
              <small class="text-muted d-block">
                Define values for each tenant for this configuration key.
              </small>
            </div>
          </div>

          <div v-for="(row, index) in tenantRows" :key="index" class="mb-2 row align-items-center">

            <div class="col-2">
              <label class="form-label">Tenant</label>
              <b-form-select
                  v-model="row.tenant"
                  :options="getTenantOptions(index)"
                  class="w-100"
                  :class="{ 'is-invalid': submitted && $v.tenantRows.$each && $v.tenantRows.$each[index].tenant.$error }"
              ></b-form-select>

              <validationMessages
                  v-if="submitted && $v.tenantRows.$each && $v.tenantRows.$each[index].tenant.$error"
                  :fieldName="'Tenant'"
                  :validationName="$v.tenantRows.$each[index].tenant"
              ></validationMessages>
            </div>

            <div class="col-9">
              <label class="form-label">Value</label>
              <b-form-input
                  v-model="row.value"
                  placeholder="Enter value"
              ></b-form-input>
            </div>

            <div class="col-1 d-flex align-items-center mt-4">
              <b-button variant="danger" @click.prevent="removeTenantRow(index)" size="sm" class="mx-auto">
                <i class="uil uil-trash-alt"></i>
              </b-button>
            </div>

          </div>

          <div class="mb-3 d-flex gap-2">
            <b-button variant="success" @click.prevent="addTenantRow" size="sm">
              <i class="uil uil-plus"></i> Add Tenant Key Value
            </b-button>

            <b-button
                variant="primary"
                @click="bulkCreateForAllTenants"
                size="sm"
                :disabled="tenantRows.length === tenants.length"
            >
              <i class="uil uil-layer-group"></i>
              Create for All Tenants
            </b-button>
          </div>

        </div>
      </div>
    </form>

    <template v-slot:modal-footer>
      <b-button variant="light" @click="closeModal">Close</b-button>
      <b-button variant="primary" @click="createTenantEnvConfig" :disabled="tryingToSubmit">
        <b-spinner v-show="tryingToSubmit" small></b-spinner>
        Submit
      </b-button>
    </template>
  </b-modal>
</template>
<style>
.swal2-container {
  z-index: 20000 !important;
}
</style>
